import {
  I2cInput,
  I2cTextarea,
  I2cDatePicker,
  I2cPhoneField,
} from "@webcomponents/i2cwebcomponents/dist/react";
import React, { useEffect } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { isValidUrl } from "../../../Utils/stringUtls";

/**
 * Component for capturing and validating project details.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.setFormValidStates - Function to set form validation states.
 * @returns {React.Component} - The rendered component.
 */
const CompanyInformation = ({
  setFormValidStates,
  setAddFormData,
  addFormData,
  formDataJson,
  isFetching,
}) => {
  // Define validation schema using Zod
  const validationSchema = z.object({
    ci_businessName: z
      .string()
      .min(1, { message: "Business Name is required" }),
    ci_physicalAddress: z
      .string()
      .min(1, { message: "Physical address is required" }),
    ci_mailingAddress: z
      .string()
      .min(1, { message: "Mailing address is required" }),
    ci_companyFax: z.string().optional(0),
    ci_companyPhone: z
      .string()
      .min(1, { message: "Company fax is required" })
      .refine((val) => isValidUrl(val)),
    ci_companyWebURL: z
      .string()
      .url({ message: "Invalid URL" })
      .min(1, { message: "website URL is required" }),
    ci_otherBusinessLoc: z.string().optional(),
    // ci_pricingSelected: z
    //   .string()
    //   .min(1, { message: "Pricing type is required" }),
    ci_date_business_established: z
      .string()
      .min(1, { message: "Business establishment date is required" }),
    // ci_timezoneReporting: z
    // 	.string()
    // 	.min(1, { message: "Reporting time zone is required" }),
  });

  // Initialize React Hook Form with Zod resolver
  const methods = useForm({
    resolver: zodResolver(validationSchema),
    mode: "all",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "firstError",
    defaultValues: {
      ci_businessName: "",
      ci_physicalAddress: "",
      ci_mailingAddress: "",
      ci_companyFax: "",
      ci_companyFaxCountry: "US",
      ci_companyPhone: "",
      ci_companyPhoneCountry: "US",
      ci_companyWebURL: "",
      ci_otherBusinessLoc: "",
      ci_date_business_established: "",
    },
  });

  const {
    control,
    formState: { errors },
  } = methods;

  const fields = [
    "ci_businessName",
    "ci_physicalAddress",
    "ci_mailingAddress",
    "ci_companyFax",
    "ci_companyPhone",
    "ci_companyPhoneCountry",
    "ci_companyWebURL",
    "ci_otherBusinessLoc",
    "ci_date_business_established",
  ];
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        methods.setValue(field, formDataJson[1]["1_2"][field] ?? "");
      });
      methods.trigger();
      handleValueChanges();
    }
  }, [isFetching]);
  // Trigger validation when component mounts
  React.useEffect(() => {
    methods.trigger();
  }, []);

  const handleValueChanges = () => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      1: {
        ...addFormData["1"],
        "1_2": { ...values },
      },
    });
  };
  // Update form validation state when errors change
  React.useEffect(() => {
    const errorsLength = Object.keys(errors).length;
    if (errorsLength === 0) setFormValidStates("1_2", true);
    else setFormValidStates("1_2", false);
  }, [Object.keys(errors).length]);

  // const numInputField = document.querySelector('.num-input-field');
  // if (numInputField && numInputField.shadowRoot) {
  //     const companyFaxInput = numInputField.shadowRoot.querySelector('[name="ci_companyFax-phone-input"]');
  //     if (companyFaxInput && companyFaxInput.shadowRoot) {
  //         const helpText = companyFaxInput.shadowRoot.querySelector('.form-control--has-help-text');
  //         if (helpText) {
  //             const formControlHelpText = helpText.querySelector('.form-control__help-text');
  //             if (formControlHelpText) {
  //                 formControlHelpText.innerHTML = "";
  //             }
  //         }
  //     }
  // }
  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-column-sm gap-row-md">
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_businessName"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="Trade or Business Name"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Trade or Business Name"
                    name={"ci_businessName"}
                    size="large"
                    required
                  />
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.ci_businessName &&
                  methods.formState.touchedFields.ci_businessName
                    ? true
                    : false
                }
                message={errors.ci_businessName?.message ?? "Invalid input"}
              /> */}
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <I2cPhoneField
                format="INTERNATIONAL"
                label="Company Phone "
                tooltip-help="Contact Number"
                name="ci_companyPhone"
                size="large"
                country-box
                required
                helpText=" "
                  selectedCountry={methods.watch("ci_companyPhoneCountry")}
                errorMessageVisible={false}
                value={methods.watch("ci_companyPhone")}
                onI2cChange={(e) => {
                    console.log(e.target.country);
                    console.log(`${e.target.countryCallingCode ? '+' : ''} ${e.target.countryCallingCode}`);
                  const country = e.target.country;
                  const isValid = e.target.isValid;
                  const value = e.target.value;
                  methods.setValue("ci_companyPhoneCountry", country);
                  methods.setValue("ci_companyPhone", value);
                  handleValueChanges();
                  if (!isValid) {
                    methods.setError("ci_companyPhone", {
                      message: e.target.guide,
                    });
                  } else {
                    methods.clearErrors("ci_companyPhone");
                  }
                  // if (isValid) {
                  // 	methods.setValue("ci_companyPhone", value);
                  // 	handleValueChanges();
                  // } else {
                  // 	methods.setError("ci_companyPhone", {
                  // 		message: e.target.guide,
                  // 	});
                  // }
                }}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_physicalAddress"
                control={control}
                render={({ field }) => (
                  <I2cTextarea
                    {...field}
                    className="single-input-field"
                    placeholder="Physical Address"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Physical Address"
                    name={"ci_physicalAddress"}
                    rows="2"
                    required
                    draggable={true}
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.ci_physicalAddress &&
                  methods.formState.touchedFields.ci_physicalAddress
                    ? true
                    : false
                }
                message={errors.ci_physicalAddress?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_mailingAddress"
                control={control}
                render={({ field }) => (
                  <I2cTextarea
                    {...field}
                    className="single-input-field"
                    placeholder="Mailing Address"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Mailing Address"
                    name={"ci_mailingAddress"}
                    rows="2"
                    required
                    draggable={true}
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.ci_mailingAddress &&
                  methods.formState.touchedFields.ci_mailingAddress
                    ? true
                    : false
                }
                message={errors.ci_mailingAddress?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              {/* <Controller
                name="ci_companyFax"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="Company Fax"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Company Fax"
                    name={"ci_companyFax"}
                  />
                )}
              /> */}
              <I2cPhoneField
                format="INTERNATIONAL"
                label="Company Fax "
                tooltip-help="Contact Number"
                name="ci_companyFax"
                size="large"
                country-box
                country={methods.watch("ci_companyFaxCountry") }
                // selectedCountry={methods.watch("ci_companyFaxCountry")}
                errorMessageVisible={false}
                // helpText=" "
                className="num-input-field" 
                value={methods.watch("ci_companyFax")}
                onI2cChange={(e) => {
                  const country = e.target.country;
                  const isValid = e.target.isValid;
                  const value = e.target.value;
                  methods.setValue("ci_companyFaxCountry", country);
                  methods.setValue("ci_companyFax", value);
                  handleValueChanges();
                  // if (isValid) {
                  // 	methods.setValue("ci_companyFax", value);
                  // 	handleValueChanges();
                  // } else {
                  // 	methods.setError("ci_companyFax", {
                  // 		message: e.target.guide,
                  // 	});
                  // }
                }}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_companyWebURL"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="Company Website URL"
                    size="large"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    type="url"
                    label="Company Website URL"
                    name={"ci_companyWebURL"}
                    required
                  />
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.ci_companyWebURL &&
                  methods.formState.touchedFields.ci_companyWebURL
                    ? true
                    : false
                }
                message={errors.ci_companyWebURL?.message ?? "Invalid input"}
              /> */}
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_otherBusinessLoc"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="Other Business Locations"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Other Business Locations"
                    name={"ci_otherBusinessLoc"}
                    size="large"
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.ci_otherBusinessLoc &&
                  methods.formState.touchedFields.ci_otherBusinessLoc
                    ? true
                    : false
                }
                message={errors.ci_otherBusinessLoc?.message ?? "Invalid input"}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="ci_date_business_established"
                control={control}
                render={({ field }) => (
                  <>
                    <I2cDatePicker
                      {...field}
                      name="ci_date_business_established"
                      onI2cDateApply={(e) => {
                        if (e.target?.appliedDate)
                          field.onChange(
                            e.target?.appliedDate[0]?.toDateString() ?? "",
                          );
                        else field.onChange("");
                        handleValueChanges();
                      }}
                      value={methods.watch("ci_date_business_established")}
                      I2cDatePicker
                      dateFormat="d-m-Y"
                      size="large"
                      label="Date Business Established"
                      // className="custom-dt-picker"
                      errorMessageVisible={false}
                      required
                      defaultDate={
                        methods.watch("ci_date_business_established")
                          ? new Date(
                              methods.watch("ci_date_business_established"),
                            )
                          : ""
                      }
                    />
                    {/* <span slot="label"></span> */}
                  </>
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.ci_date_business_established &&
                  methods.formState.touchedFields.ci_date_business_established
                    ? true
                    : false
                }
                message={
                  errors.ci_date_business_established?.message ??
                  "Invalid input"
                }
              /> */}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CompanyInformation;
